import React, { useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import {
  DataFetcher,
  Store,
  middlewareTypes,
  createInstrumentationMiddleware,
  createErrorMiddleware,
} from '@bingads-webui-react/overreact';
import { ServerContext } from '@bingads-webui-campaign-react/mca-server-context';
import { getRequestor, middlewares as mcaMiddlewares, getEnvironmentInstance } from '@bingads-webui/overreact-odata-multi-channel';
import { useCCEnvironment } from '@bingads-webui/overreact-cc';
import { getRequestor as getCampaignRequestor, getEnvironmentInstance as getCampaignEnvInstance } from '@bingads-webui/overreact-odata-campaign';
import { getRequestor as getSmartRequestor, getEnvironmentInstance as getSmartEnvInstance } from '@bingads-webui/overreact-smart';
import { getConfig } from '@bingads-webui-campaign/odata-config';
import { InstrumentationContext, isExpectedErrorCodes } from '@bingads-webui-campaign-react/mca-instrumentation';
import { getDefaultHeaderObj } from '@bingads-webui/overreact-bingads';

const store = new Store();

function useMCAEnvironment(params) {
  const {
    serviceRoot, token, applicationSource, middlewares,
  } = params;

  return useMemo(() => {
    const requestor = getRequestor(
      serviceRoot,
      {
        ...getDefaultHeaderObj(),
        Authorization: `CCMTSmallToken ${token}`,
        'x-ms-applicationsource': applicationSource,
      }
    );

    return getEnvironmentInstance(requestor, store, middlewares);
  }, [applicationSource, middlewares, serviceRoot, token]);
}

function useCampaignEnvironment(params) {
  const {
    serviceRoot, token, middlewares,
  } = params;

  return useMemo(() => {
    const requestor = getCampaignRequestor(
      serviceRoot,
      {
        ...getDefaultHeaderObj(),
        Authorization: `CCMTSmallToken ${token}`,
      }
    );

    return getCampaignEnvInstance(requestor, store, middlewares);
  }, [middlewares, serviceRoot, token]);
}

function useSmartEnvironment(params) {
  const {
    serviceRoot, token, middlewares,
  } = params;

  return useMemo(() => {
    const requestor = getSmartRequestor({
      serviceRoot,
      token,
    });

    return getSmartEnvInstance(requestor, middlewares);
  }, [middlewares, serviceRoot, token]);
}

export const OverreactDataFetcher = (props) => {
  const [{
    McaServiceRoot,
    CampaignAPIPath, // this value only exists in CCUI ServerContext
    Token,
    CurrentLocale,
    ServicesEndpoints: {
      ClientCenterUIAPI,
      CampaignAPI, // path to ODataApi root, should have trailing slash
    },
    PageTrackingId,
    CultureLcid,
    Urls: {
      SignOutUrl,
    } = {},
  }] = useContext(ServerContext);
  const instrumentationContext = useContext(InstrumentationContext) || [];
  const { instrumentation } = instrumentationContext[0] || {};
  const activity = useMemo(
    () => {
      const scenario = instrumentation && instrumentation.overreactRequest.create();
      const act = scenario ? scenario.logger.create() : {
        trace: _.noop,
        error: _.noop,
        ajax: _.noop,
      };
      return act;
    },
    [instrumentation]
  );

  const errorProcessor = useCallback((error) => {
    if (error && error.status === 401 && SignOutUrl) {
      window.location.href = SignOutUrl;
    }
  }, [SignOutUrl]);

  const instrumentationMiddleware = {
    [middlewareTypes.ERROR]: createErrorMiddleware({
      errorProcessor,
    }),
    [middlewareTypes.INSTRUMENTATION]: createInstrumentationMiddleware({
      pageTrackingId: PageTrackingId,
      // traceFunc is called on error and the error is mapped to user error using errorMappers
      // errorFunc is called on error (no result - server or network error)
      // perfFunc is called pre and post request
      loggerFunc: {
        traceFunc: ({
          requestId, message, api, httpMethod,
        }) => {
          activity.trace(message || '', api, requestId, httpMethod);
        },
        errorFunc: ({
          requestId, api, httpMethod, message,
        }) => {
          activity.error(message || '', api, requestId, null /* impact user */, httpMethod);
        },
        perfFunc: ({
          requestId, api, httpMethod, timeTaken, message, isMethodEnter,
        }) => {
          activity.ajax(
            requestId, api, isMethodEnter,
            httpMethod, timeTaken, null /* pass */, message
          );
        },
      },
      errorMappers: [
        {
          check: (error) => {
            const errorArray = error.responseJSON && Array.isArray(error.responseJSON.value)
              ? error.responseJSON.value
              : error.responseJSON;
            return isExpectedErrorCodes(errorArray) || isExpectedErrorCodes(_.get(error, 'Errors'));
          },
        },
      ],
      stubOptions: { lcid: CultureLcid },
    }),
  };
  const allMiddlewares = { ...mcaMiddlewares, ...instrumentationMiddleware };

  const campaignOdataRoot = useMemo(() => {
    const odataConfig = getConfig();
    const odataPath = CampaignAPIPath || odataConfig.odataPath;
    return `${CampaignAPI}${odataPath}`;
  }, [CampaignAPI, CampaignAPIPath]);

  const overreactMcaEnv = useMCAEnvironment({
    serviceRoot: McaServiceRoot,
    token: Token,
    middlewares: allMiddlewares,
    applicationSource: 'up',
  });

  const overreactCcEnv = useCCEnvironment({
    serviceRoot: ClientCenterUIAPI,
    store,
    middlewares: allMiddlewares,
    locale: CurrentLocale,
  });

  const overreactCampaignEnv = useCampaignEnvironment({
    serviceRoot: campaignOdataRoot,
    token: Token,
    middlewares: allMiddlewares,
  });

  const overreactSmartEnv = useSmartEnvironment({
    serviceRoot: campaignOdataRoot,
    token: Token,
    middlewares: instrumentationMiddleware,
  });

  return (
    <DataFetcher
      environment={overreactMcaEnv}
      environments={[overreactMcaEnv, overreactCcEnv, overreactCampaignEnv, overreactSmartEnv]}
    >
      {props.children}
    </DataFetcher>
  );
};

OverreactDataFetcher.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
